import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BillingAuditList } from "@/components/AdminBillingAudit";
import { X, Loader2, Pause, Play, Ban, Gift, CalendarPlus, Crown, Landmark, Undo2, BadgeCheck, ScrollText } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const STATUS_STYLE = {
  Active: "bg-emerald-50 text-emerald-600", Lifetime: "bg-purple-50 text-purple-700",
  Suspended: "bg-slate-100 text-slate-500", Cancelled: "bg-red-50 text-red-600",
  "Past Due": "bg-red-50 text-red-600", Inactive: "bg-amber-50 text-amber-600", Trial: "bg-cobalt-light text-cobalt",
};

// Super Admin billing controls for one attorney: instant suspend/reactivate/cancel,
// recorded refunds, comp/extend/grant, lifetime memberships, Founder grandfathering.
export default function AdminBillingModal({ attorney, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [months, setMonths] = useState(1);
  const [days, setDays] = useState(30);
  const [planId, setPlanId] = useState("");
  const [founderPrice, setFounderPrice] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/admin/billing/${attorney.user_id}/summary`)
    .then(({ data }) => setData(data))
    .catch(() => toast.error("Could not load billing summary"));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (action, extra = {}) => {
    if (reason.trim().length < 3) { toast.error("A reason is required for every billing action."); return; }
    setBusy(action + (extra.txn_id || ""));
    try {
      await api.post(`/admin/billing/${attorney.user_id}/action`, { action, reason: reason.trim(), ...extra });
      toast.success(`Done — ${action} recorded in the audit log`);
      setReason(""); load(); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };

  const m = data?.membership || {};
  const Btn = ({ action, extra, icon: Icon, label, cls, testid }) => (
    <button onClick={() => act(action, extra)} disabled={!!busy} data-testid={testid || `billing-action-${action}`}
      className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-all disabled:opacity-50 ${cls}`}>
      {busy === action + (extra?.txn_id || "") ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {label}
    </button>
  );
  const numCls = "w-20 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt";

  return (
    <div className="fixed inset-0 z-[100] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-billing-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="font-heading font-bold text-navy text-lg">Billing Controls — {attorney.name}</div>
            <div className="text-xs text-slate-500">{attorney.email}{attorney.firm_name ? ` · ${attorney.firm_name}` : ""}</div>
          </div>
          <button onClick={onClose} data-testid="billing-modal-close" className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        {!data ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div> : (
          <div className="p-6 space-y-6">
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 flex items-center gap-3 flex-wrap" data-testid="billing-modal-status">
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_STYLE[m.display_status] || "bg-slate-100 text-slate-600"}`}>{m.display_status}</span>
              <span className="text-sm text-navy font-medium">{m.membership_level || "No plan"} · {money(m.monthly_price)}/mo</span>
              {m.lifetime && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 flex items-center gap-1"><Crown className="w-3 h-3" /> Lifetime</span>}
              {m.grandfathered && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 flex items-center gap-1"><Landmark className="w-3 h-3" /> Grandfathered</span>}
              {m.comped && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-cobalt-light text-cobalt">Comped</span>}
              {m.manually_granted && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600">Manually granted</span>}
              {!m.lifetime && m.current_period_end && <span className="text-xs text-slate-500">Period ends {new Date(m.current_period_end).toLocaleDateString()}</span>}
              {m.promo_label && <span className="text-xs text-amber-700">{m.promo_label}</span>}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Reason (required — recorded permanently)</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="billing-reason-input"
                placeholder="e.g. Strategic partner agreement, chargeback resolution, goodwill credit…"
                className="mt-1.5 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cobalt min-h-[64px]" />
            </div>

            <div>
              <h4 className="font-heading font-semibold text-navy text-sm mb-2">Immediate access</h4>
              <div className="flex gap-2 flex-wrap">
                <Btn action="suspend" icon={Pause} label="Suspend now" cls="border-red-200 text-red-600 bg-red-50 hover:bg-red-100" />
                <Btn action="reactivate" icon={Play} label="Reactivate now" cls="border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100" />
                <Btn action="cancel" icon={Ban} label="Cancel membership" cls="border-slate-200 text-slate-600 bg-white hover:bg-red-50 hover:text-red-600" />
              </div>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-navy text-sm mb-2">Grant, comp & extend (no payment required)</h4>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="number" min={1} value={months} onChange={(e) => setMonths(Number(e.target.value) || 1)} data-testid="billing-months-input" className={numCls} />
                  <span className="text-xs text-slate-500">month(s)</span>
                  <select value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid="billing-plan-select"
                    className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt">
                    <option value="">Keep current plan</option>
                    {(data.plans || []).map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name} — {money(p.monthly_price)}/mo</option>)}
                  </select>
                  <Btn action="grant" extra={{ months, ...(planId ? { plan_id: planId } : {}) }} icon={BadgeCheck} label="Grant membership" cls="border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100" />
                  <Btn action="comp" extra={{ months }} icon={Gift} label={`Comp ${months} mo free`} cls="border-cobalt/20 text-cobalt bg-cobalt-light hover:bg-cobalt hover:text-white" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="number" min={1} value={days} onChange={(e) => setDays(Number(e.target.value) || 1)} data-testid="billing-days-input" className={numCls} />
                  <span className="text-xs text-slate-500">day(s)</span>
                  <Btn action="extend" extra={{ days }} icon={CalendarPlus} label="Extend period" cls="border-cobalt/20 text-cobalt bg-cobalt-light hover:bg-cobalt hover:text-white" />
                  <Btn action="lifetime" icon={Crown} label="Grant lifetime membership" cls="border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="number" min={0} value={founderPrice} onChange={(e) => setFounderPrice(e.target.value)} data-testid="billing-founder-price-input" placeholder="$/mo" className={numCls} />
                  <Btn action="grandfather" extra={founderPrice !== "" ? { monthly_price: Number(founderPrice) } : {}} icon={Landmark} label="Grandfather Founder pricing" cls="border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100" />
                  <span className="text-xs text-slate-400">Locks their rate forever (optional price override)</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-navy text-sm mb-2">Refunds <span className="text-[10px] font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Recorded — no live money movement in sandbox</span></h4>
              {(data.transactions || []).length === 0
                ? <p className="text-sm text-slate-400">No paid transactions.</p>
                : <div className="space-y-2">
                    {data.transactions.map((t) => (
                      <div key={t.txn_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-2.5" data-testid={`billing-txn-${t.txn_id}`}>
                        <div className="text-sm">
                          <span className="font-medium text-navy">{money(t.amount)}</span>
                          <span className="text-slate-500"> · {(t.metadata?.label) || t.kind?.replace(/_/g, " ")} · {new Date(t.paid_at || t.created_at).toLocaleDateString()}</span>
                        </div>
                        {t.refunded
                          ? <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">Refunded {money(t.refund_amount)}</span>
                          : <Btn action="refund" extra={{ txn_id: t.txn_id }} icon={Undo2} label="Refund" cls="border-slate-200 text-slate-600 bg-white hover:bg-amber-50 hover:text-amber-700" testid={`billing-refund-${t.txn_id}`} />}
                      </div>
                    ))}
                  </div>}
            </div>

            <div>
              <h4 className="font-heading font-semibold text-navy text-sm mb-2 flex items-center gap-1.5"><ScrollText className="w-4 h-4 text-cobalt" /> Audit trail for this attorney</h4>
              {(data.audit || []).length === 0
                ? <p className="text-sm text-slate-400">No billing actions recorded yet.</p>
                : <BillingAuditList items={data.audit} compact />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
